import { useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { CrawlingBorder } from './components/CrawlingBorder';
import { PipelineColumnHeader } from './components/PipelineColumnHeader';
import { PipelineConnector } from './components/PipelineConnector';
import { PipelineDocumentCard } from './components/PipelineDocumentCard';
import { PipelineExtractionCard } from './components/PipelineExtractionCard';
import { PipelineSourceCard } from './components/PipelineSourceCard';
import { getDemoConfig } from './config';
import { DocumentsUploadPipelineProps } from './types';
import { useDemoPipelineSimulation } from './useDemoPipelineSimulation';

export function DocumentsUploadPipeline({
    fileName,
    fileSize,
}: DocumentsUploadPipelineProps = {}) {
    const DEMO_CONFIG = getDemoConfig(fileName, fileSize);

    // Use the custom hook for simulation logic
    const {
        stage,
        uploadProgress,
        documents,
        documentProgress,
        sortedExtractedData,
        isCol1Active,
        isCol2Active,
        isCol3Active,
    } = useDemoPipelineSimulation({ fileName, fileSize });

    // Refs for connector positioning
    const containerRef = useRef<HTMLDivElement>(null);
    const sourceCardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const documentCardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const extractionCardRefs = useRef<(HTMLDivElement | null)[]>([]);

    const setSourceCardRef = (el: HTMLDivElement | null) => {
        sourceCardRefs.current[0] = el;
    };

    const setDocumentCardRef =
        (index: number) => (el: HTMLDivElement | null) => {
            documentCardRefs.current[index] = el;
        };

    const setExtractionCardRef =
        (docId: number, index: number) => (el: HTMLDivElement | null) => {
            const docIndex = documents.findIndex((d) => d.id === docId);
            if (docIndex !== -1) {
                extractionCardRefs.current[docIndex] = el;
            }
        };

    return (
        <div className="space-y-8 p-8">
            {/* Pipeline Grid */}
            <div
                ref={containerRef}
                className="relative grid grid-cols-3 gap-16 items-start"
            >
                {/* SVG Connectors: Source → Documents (fan-out) */}
                <PipelineConnector
                    startRefs={sourceCardRefs}
                    endRefs={documentCardRefs}
                    containerRef={containerRef}
                    isVisible={documents.length > 0}
                    oneToOne={false}
                    itemCount={documents.length}
                />

                {/* SVG Connectors: Documents → Extractions (1:1) */}
                <PipelineConnector
                    startRefs={documentCardRefs}
                    endRefs={extractionCardRefs}
                    containerRef={containerRef}
                    isVisible={sortedExtractedData.length > 0}
                    oneToOne={true}
                    itemCount={sortedExtractedData.length}
                    progressState={documentProgress}
                />

                {/* Column 1: Source */}
                <div className="flex flex-col">
                    <PipelineColumnHeader
                        title="Upload"
                        subtitle="Source File"
                    />
                    <CrawlingBorder
                        isActive={isCol1Active}
                        className="min-h-[200px]"
                    >
                        <div className="p-6 flex items-center justify-center h-full">
                            <AnimatePresence mode="wait">
                                {stage !== 'idle' && (
                                    <motion.div
                                        ref={setSourceCardRef}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <PipelineSourceCard
                                            isUploading={stage === 'uploading'}
                                            progress={uploadProgress}
                                            isComplete={stage !== 'uploading'}
                                            fileName={
                                                DEMO_CONFIG.sourceFile.name
                                            }
                                            fileSize={
                                                DEMO_CONFIG.sourceFile.size
                                            }
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </CrawlingBorder>
                </div>

                {/* Column 2: Discovery */}
                <div className="flex flex-col">
                    <PipelineColumnHeader
                        title="Discovery"
                        subtitle="Document Splitting"
                    />
                    <CrawlingBorder
                        isActive={isCol2Active}
                        className="min-h-[240px]"
                    >
                        <div className="p-6 space-y-4">
                            {stage !== 'idle' && stage === 'uploading' ? (
                                <div className="space-y-4">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div
                                            key={i}
                                            className="h-16 min-h-[80px] bg-muted rounded-lg animate-pulse"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <AnimatePresence>
                                    {documents.map((doc, index) => (
                                        <motion.div
                                            key={doc.id}
                                            ref={setDocumentCardRef(index)}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.15 }}
                                        >
                                            <PipelineDocumentCard
                                                document={doc}
                                                progress={
                                                    documentProgress[doc.id]
                                                }
                                            />
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            )}
                        </div>
                    </CrawlingBorder>
                </div>

                {/* Column 3: Extraction */}
                <div className="flex flex-col">
                    <PipelineColumnHeader
                        title="Extraction"
                        subtitle="Metadata Analysis"
                    />
                    <CrawlingBorder
                        isActive={isCol3Active}
                        className="min-h-[500px]"
                    >
                        <div className="p-6 space-y-4">
                            {stage ===
                            'idle' ? null : sortedExtractedData.length === 0 ? (
                                <div className="space-y-4">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div
                                            key={i}
                                            className="h-24 min-h-[100px] bg-muted rounded-lg animate-pulse"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <AnimatePresence>
                                        {sortedExtractedData.map(
                                            (data, index) => (
                                                <motion.div
                                                    key={data.id}
                                                    ref={setExtractionCardRef(
                                                        data.id,
                                                        index,
                                                    )}
                                                    initial={{
                                                        opacity: 0,
                                                        x: -20,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        x: 0,
                                                    }}
                                                    transition={{
                                                        delay: index * 0.1,
                                                    }}
                                                >
                                                    <PipelineExtractionCard
                                                        data={data}
                                                        progress={
                                                            documentProgress[
                                                                data.id
                                                            ]
                                                        }
                                                        documents={documents}
                                                    />
                                                </motion.div>
                                            ),
                                        )}
                                    </AnimatePresence>
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            3 - sortedExtractedData.length,
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`skeleton-${i}`}
                                            className="h-24 min-h-[150px] bg-muted rounded-lg animate-pulse"
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    </CrawlingBorder>
                </div>
            </div>
        </div>
    );
}
